<script lang="ts" setup>
import type { Blocks, BreadcrumbListItem, ProductListResponse, Metadata, ContentResponseContent } from '@/types/index';

const props = defineProps<{
  content: ContentResponseContent;
  blocks: Blocks;
  breadcrumbs: BreadcrumbListItem[];
  metadata: Metadata;
}>();

const { path, query } = useRoute();
const { addOrUpdateQueryParam, clearQueryParams } = useFilters();
const { getListContent } = useContentApi();
const layout = useLayout();
const defaultLoadProductAmounts = layout.isMobile || layout.isTablet ? 8 : 12;
const amountProductsToFetch = ref<number>(defaultLoadProductAmounts);
const maxProductsToGet = ref<number>(defaultLoadProductAmounts);
const productList = ref<ProductListResponse>();
const productsLoading = ref<boolean>(true);
const selectedTags = (query.tags as string) || '';
const loggingService = useLoggingService();
const currentPage = shallowRef<number>(1);

const amountProductsAfterFilters = computed(() => {
  return productList?.value?.totalItems || 0;
});

const blocks = computed(() => {
  return props.blocks.blocks || {};
});

async function getList(fetchMore: boolean, tagsParam: string = '', pageNumber: number = 1) {
  if (fetchMore) {
    amountProductsToFetch.value = Math.min(
      maxProductsToGet.value,
      amountProductsToFetch.value + defaultLoadProductAmounts,
    );
    productsLoading.value = true;
  }
  try {
    const { data } = await useAsyncData(`ProductListPageListFetch-${currentPage.value}`, () =>
      getListContent<ProductListResponse>(
        useFrontendBase(path),
        pageNumber,
        amountProductsToFetch.value,
        tagsParam,
        true,
      ),
    );

    if (data.value) {
      productList.value = data.value;
      maxProductsToGet.value = data.value.totalItems;
      productsLoading.value = false;
    }
  }
  catch (error) {
    loggingService.error(error);
  }
}

async function handleTagFiltersUpdated(paramValue: string) {
  if (paramValue !== '') {
    addOrUpdateQueryParam('tags', paramValue.toLowerCase());
  }
  else {
    clearQueryParams();
  }
  amountProductsToFetch.value = defaultLoadProductAmounts;
  await getList(false, paramValue.toLowerCase());
}

if (selectedTags !== '') {
  await getList(false, selectedTags);
}
else {
  await getList(false);
}

async function setCurrentPage(page: number = 1) {
  currentPage.value = page;
  await getList(false, '', currentPage.value);
}

if (query.page) {
  currentPage.value = Number(query.page);
  await getList(false, '', Number(query.page));
}

const calculateSurroundingPages = computed(() => {
  if (layout.isMobile || layout.isXsMobile) {
    if (currentPage.value === 1 || currentPage.value === productList.value?.totalPages) {
      return 2;
    }
    else {
      return 1;
    }
  }
  else if (currentPage.value === 1 || currentPage.value === productList.value?.totalPages) {
    return 4;
  }
  else if (currentPage.value === 2 || currentPage.value + 1 === productList.value?.totalPages) {
    return 3;
  }
  else {
    return 2;
  }
});
</script>

<template>
  <section class="flex flex-col">
    <section class="container grid grid-cols-12 md:gap-8">
      <div class="col-span-12 flex flex-col items-baseline justify-between gap-y-4 py-10 sm:flex-row">
        <h1 class="large-title">{{ $t('ProductList.Products') }}</h1>
        <p class="text-h4 font-semibold">{{ `${amountProductsAfterFilters} ${$t('ProductList.Products')}` }}</p>

        <BaseCombobox
          :items="productList?.filters.tags"
          :allow-multiple-options="false"
          dropdown-id="dropdown-filters"
          :placeholder="$t('General.All')"
          dropdown-label="Filters"
          @select="handleTagFiltersUpdated"
          @single-selected="handleTagFiltersUpdated"
        />
      </div>

      <div class="col-span-12 md:col-span-2">
        <BaseListFilter
          v-if="productList?.filters?.tags && productList?.filters?.tags.length > 0"
          :tag-filters="productList?.filters?.tags"
          class="flex flex-wrap md:flex-col"
          :query-tag="selectedTags"
          :aria-label="$t('Aria.ProductListPage.ProductList')"
          @tag-filters-changed="handleTagFiltersUpdated"
        />
      </div>

      <section
        v-if="productList?.items"
        class="col-span-12 mb-10 flex flex-col items-center md:col-span-10"
      >
        <PartialsProductList
          :loading="productsLoading"
          :products="productList.items"
          class="mb-8"
        />

        <PartialsPagination
          :current-page="currentPage"
          :surrounding-pages-displayed="calculateSurroundingPages"
          :total-pages="productList.totalPages"
          @page-updated="setCurrentPage"
        />
      </section>

      <BlockList
        v-if="blocks"
        :blocks="blocks"
      />
    </section>
  </section>
</template>
